import { GetServerSideProps, InferGetStaticPropsType } from 'next';
import { NextPageWithLayout } from '@/pages/_app';
import Head from 'next/head';
import Layout from '@/layouts';
import { AppSettings } from '@/shared/app-common';
import { getInsFamousTotalData, getStartDataListApiInner, getStartLabels } from '@/lib/service';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import { canonicalUrl } from '@/shared/utils';
import { getTdkApi } from '@/lib/service/tdk';
import { page3JsonLd } from '@/lib/jsonld';
import { hyphenatedToTitleCase, removeLastPathSegment } from '@/shared/path-helper';

const StarData = dynamic(() => import('@/components/startData'), {
  ssr: true,
});

export const getServerSideProps: GetServerSideProps = async (context) => {
  const {
    locale,
    req,
    resolvedUrl,
    params: { label },
  } = context;

  const [articleListRes, localeRes, totalRes, labelRes, tdkRes] = await Promise.all([
    getStartDataListApiInner(
      {
        page_no: 1,
        page_size: 30,
        search_word: '',
        label: `${label}`,
        order_column: 'hot',
      },
      locale
    ),
    serverSideTranslations(locale, ['common']),
    getInsFamousTotalData(),
    getStartLabels(locale),
    getTdkApi(resolvedUrl.split('?')[0], locale),
  ]);
  const tempData = localeRes['_nextI18Next']['initialI18nStore'][locale]['common'] ?? {};
  const parentPath = removeLastPathSegment(resolvedUrl);
  const siteUrl = locale !== 'en' ? `https://${req.headers.host}/${locale}` : `https://${req.headers.host}`;
  const parentUrl = siteUrl + parentPath;
  const parentTitle = hyphenatedToTitleCase(parentPath);
  const jsonLd = page3JsonLd(
    {
      title: `${label}`,
      desc: tdkRes?.data?.description ?? tempData['StarDataFilterDesc1'],
      url: canonicalUrl(req.headers.host, resolvedUrl, locale),
    },
    {
      title: parentTitle,
      url: parentUrl,
    },
    {
      url: siteUrl,
    }
  );
  return {
    props: {
      ...localeRes,
      StarDataList: { ...(articleListRes ?? {}) },
      labelList: labelRes?.data ?? [],
      herf: canonicalUrl(req.headers.host, resolvedUrl, locale),
      count: totalRes?.data ?? 0,
      jsonLd,
    },
  };
};

const CategoryPage: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = (props) => {
  return <StarData data={props} />;
};

CategoryPage.getLayout = function getLayout(page: React.ReactElement) {
  const title = page?.props?.title;
  const desc = page?.props?.description;

  return (
    <Layout
      head={
        <Head>
          <title>{title}</title>
          <meta name="title" content={title} />
          <meta name="description" content={desc} />

          <meta property="og:title" content={title} />
          <meta property="og:description" content={desc} />
          <meta property="og:image" content={AppSettings.logoUrl} />
          <meta property="og:image:secure_url" content={AppSettings.logoUrl} />
          <meta property="og:type" content="summary" />
          <meta property="og:url" content={page.props?.herf} />

          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={desc} />
          <meta name="twitter:image" content={AppSettings.logoUrl} />
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:site" content={page.props?.herf} />

          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <link rel="icon" href={AppSettings.icoPath} />
          <link rel="canonical" href={page.props?.herf} />
          {page.props?.jsonLd && (
            <script
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(page.props?.jsonLd) }}
            />
          )}
        </Head>
      }
      // showHeader={false}
      // showFooter={false}
    >
      {page}
    </Layout>
  );
};

export default CategoryPage;
